"use client";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";

import { BotIcon } from "./ui/bot";
import {
  startTransition,
  useActionState,
  useState,
  useRef,
  useEffect,
} from "react";
import {
  ActionState,
  saveDataAction,
} from "@/features/kalkulator-data-internet/actions/save-data.action";
import {
  type DataInternetInput,
  DataInternetSchema,
} from "@/features/kalkulator-data-internet/schemas/kalkulator-data-internet.schema";

import { Loader2 } from "lucide-react";
import { Separator } from "./ui/separator";

import { motion, AnimatePresence } from "motion/react";
import { Badge } from "./ui/badge";
import { toThousandSeparator } from "@/features/kalkulator-data-internet/utils/hitung-data-internet";
import { cn } from "@/lib/utils";

const initialState: ActionState = { success: false, message: "" };
export function CardFormKalkulator() {
  const [state, formAction, isPending] = useActionState(
    saveDataAction,
    initialState,
  );
  const [key, setKey] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);
  const {
    register,
    reset,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<DataInternetInput>({
    resolver: zodResolver(DataInternetSchema),
    defaultValues: {
      bitrate: undefined,
      timestamp: "",
      hari: undefined,
    },
  });

  const resultRef = useRef<HTMLDivElement | null>(null);
  const formRef = useRef<HTMLFormElement | null>(null);

  const handleReset = () => {
    reset();

    setKey((prev) => prev + 1);
    setShowSuccess(false); // Sembunyikan notifikasi secara manual
  };

  useEffect(() => {
    if (!isPending && state.success) {
      const timer = setTimeout(() => {
        if (resultRef.current) {
          resultRef.current.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, 10); // delay lebih lama biar DOM pasti ready

      return () => clearTimeout(timer);
    }
  }, [isPending, state.success]);
  useEffect(() => {
    if (state?.success) {
      setShowSuccess(true);
    }
  }, [state]);
  const menit = state.result?.menit ?? 0;
  const jam = state.result?.jam ?? 0;
  const dataInternet = Number(state.result?.dataInternet) ?? 0;
  const totalMenit = state.result?.totalMenit ?? 0;
  const totalJam = state.result?.totalJam ?? 0;
  return (
    <Card className="w-full max-w-md">
      <CardContent>
        <form
          ref={formRef}
          key={key}
          action={formAction}
          onSubmit={handleSubmit((data) => {
            const formData = new FormData();
            Object.entries(data).forEach(([key, val]) =>
              formData.append(key, val.toString()),
            );

            startTransition(() => {
              formAction(formData);
            });
          })}
          onReset={() => setKey((prev) => prev + 1)}
        >
          <FieldGroup>
            <Field>
              <div className="flex items-center">
                <FieldLabel htmlFor="bitrate">Bitrate (Kbps)</FieldLabel>
                <p className="ml-auto text-muted-foreground text-xs mr-px">
                  Rekomendasi bitrate ?
                  <a
                    href="https://support.google.com/youtube/answer/2853702?hl=en"
                    target="_blank"
                    rel="noopener"
                    className="text-primary underline pl-0.5"
                  >
                    Cek disini
                  </a>
                </p>
              </div>
              <Input
                className={`${
                  errors.bitrate
                    ? "border-red-500 focus:ring-red-200"
                    : "border-gray-300 focus:ring-blue-200"
                } `}
                id="bitrate"
                placeholder="0"
                min={0}
                type="string"
                aria-invalid={!!errors.bitrate}
                {...register("bitrate", {
                  onChange: (e) => {
                    const formatted = toThousandSeparator(e.target.value);
                    setValue("bitrate", formatted as any);
                  },
                })}
              />
              {errors.bitrate && (
                <FieldDescription className="text-red-500 text-xs">
                  {errors.bitrate.message}
                </FieldDescription>
              )}
            </Field>

            <div className="grid md:grid-cols-2 gap-4">
              <Field>
                <FieldLabel htmlFor="duration">
                  Durasi per hari
                  <span className="text-muted-foreground -ml-1">
                    (Jam:Menit)
                  </span>
                </FieldLabel>
                <Input
                  {...register("timestamp")}
                  type="time"
                  id="duration"
                  step="60"
                  aria-invalid={!!errors.timestamp}
                  className={cn(
                    "appearance-none bg-background text-sm border transition",
                    "[&::-webkit-calendar-picker-indicator]:hidden",
                    errors.timestamp
                      ? "border-red-500 focus:ring-2 focus:ring-red-200"
                      : "border-input focus:ring-2 focus:ring-ring/50",
                  )}
                />
                {errors.timestamp && (
                  <FieldDescription className="text-red-500 text-xs mt-1">
                    {errors.timestamp.message}
                  </FieldDescription>
                )}
              </Field>
              <Field>
                <FieldLabel htmlFor="hari">Hari</FieldLabel>
                <Input
                  className="text-sm"
                  placeholder="0"
                  id="hari"
                  type="number"
                  min={0}
                  {...register("hari")}
                  aria-invalid={!!errors.hari}
                />
                {errors.hari && (
                  <FieldDescription className="text-red-500 text-xs mt-1">
                    {errors.hari.message}
                  </FieldDescription>
                )}
              </Field>
            </div>

            <Field
              orientation="horizontal"
              className="flex-col items-stretch md:flex md:flex-row-reverse "
            >
              <Button type="submit" disabled={isPending}>
                {" "}
                {isPending ? "Loading..." : "Hitung"}
              </Button>
              <Button type="button" variant="outline" onClick={handleReset}>
                Reset
              </Button>
            </Field>
          </FieldGroup>
          <Separator className="my-7" />
          {/* !loading & !result */}
          <AnimatePresence mode="wait">
            {!showSuccess && !isPending && (
              <motion.div
                key="default"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-10 text-center pt-1 pb-4.5"
              >
                <div className="text-7xl mb-4 animate-float inline-block">
                  <BotIcon size={52} />
                </div>
                <p className="text-xl font-bold text-foreground mb-2">
                  Siap Hitung?
                </p>
                <p className="text-muted-foreground">
                  Masukkan jumlah bitrate, waktu dan hari Anda untuk mendapatkan
                  hasil data internet yang digunakan
                </p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Animation */}
          <AnimatePresence initial={false}>
            {isPending && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-10 p-2 bg-card rounded-lg  animate-slide-in-up"
              >
                <div className="flex flex-col items-center justify-center space-y-4">
                  <div className="relative w-20 h-10">
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Loader2 className="w-8 h-8 text-primary animate-spin" />
                    </div>
                  </div>
                  <div className="text-center space-y-2">
                    <h3 className="text-xl font-bold text-foreground">
                      Menghitung
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      Harap tunggu sebentar...
                    </p>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Animation */}
          <AnimatePresence initial={false}>
            {!isPending && showSuccess && (
              <motion.div
                key="card"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                ref={resultRef}
                transition={{
                  type: "spring",
                  stiffness: 120,
                  damping: 12,
                }}
              >
                <Card className="my-7">
                  <CardHeader>
                    <CardTitle className="gap-y-0.5 flex flex-col text-white h-25  items-center rounded-md justify-center bg-slate-800 ">
                      <h4 className="text-[0.8rem] sm:text-sm font-light">
                        Data Internet
                      </h4>
                      <span className="text-4xl sm:text-5xl font-medium tracking-tight">
                        ±{" "}
                        <b>
                          {state.result?.dataInternet}{" "}
                          {state.result?.satuanByte}
                        </b>
                      </span>
                    </CardTitle>
                  </CardHeader>
                  {dataInternet !== 0 && (
                    <CardContent>
                      <small className="text-sm leading-none font-medium">
                        Keterangan
                      </small>
                      <ul className="my-3 ml-6 list-disc [&>li]:mt-2">
                        <li>
                          Dengan bitrate sebesar {state.result?.bitrate} Kbps
                        </li>

                        <li>
                          Live streaming {jam > 0 && `${jam} jam`}
                          {jam > 0 && menit > 0 && " dan "}
                          {menit > 0 && `${menit} menit`} per hari
                        </li>
                        <li>
                          Selama {state.result?.hari} hari (total{" "}
                          {totalJam != 0 &&
                            `${totalJam} jam`}
                            {totalJam != 0 && totalMenit != 0 && " "}
                          {totalMenit > 0 &&
                            `${totalMenit} menit`})
                        </li>
                      </ul>
                    </CardContent>
                  )}
                  {dataInternet !== 0 && (
                    <CardFooter className="flex flex-col items-start gap-y-1">
                      <Badge>Catatan</Badge>
                      <p className="leading-7 not-first:mt-1">
                        Besaran data internet yang digunakan dari hasil
                        perhitungan hanya perkiraan, bisa lebih atau kurang
                        tetapi tidak jauh berbeda dari{" "}
                        <span className="font-semibold">
                          {state.result?.dataInternet}{" "}
                          {state.result?.satuanByte}
                        </span>
                        , hasil perhitungan.
                      </p>
                    </CardFooter>
                  )}
                </Card>
              </motion.div>
            )}
          </AnimatePresence>
        </form>
      </CardContent>
    </Card>
  );
}
